import { SeoLandingLayout, SeoLandingPageData } from '../../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Plan de trésorerie PME : exemple et méthode pas à pas | Trezoone',
    description: 'Construisez le plan de trésorerie de votre PME avec un exemple concret. Gérez le BFR, les délais clients et les charges variables.',
    canonical: '/plan-tresorerie-pme',
    keywords: 'plan de trésorerie PME, plan tresorerie exemple, plan tresorerie PME exemple, plan de financement PME, trésorerie PME',
  },
  hero: {
    tag: 'Plan trésorerie PME',
    title: 'Votre PME fait du chiffre. Mais votre trésorerie, elle, ne suit pas.',
    subtitle: 'Le plan de trésorerie PME est votre outil de survie financière. Voici comment le construire avec un exemple réel et éviter les pièges classiques.',
    ctaLabel: 'Construire mon plan',
  },
  pilierLink: { label: 'Guide plan de trésorerie', href: '/plan-tresorerie' },
  sections: [
    {
      type: 'hook',
      title: 'Le problème spécifique des PME',
      content: 'Une PME a des flux de trésorerie plus complexes qu\'une petite structure :',
      items: [
        'Plusieurs clients avec des délais de paiement différents (30, 45, 60, 90 jours)',
        'Des cycles de vente longs qui créent de gros décalages entre facturation et encaissement',
        'Des charges sociales trimestrielles qui créent des pics de décaissement',
        'Des investissements récurrents qui immobilisent du cash',
        'Une TVA à avancer avant de la récupérer',
      ],
    },
    {
      type: 'example',
      title: 'Exemple de plan de trésorerie PME — secteur BTP, 12 personnes',
      content: 'CA annuel : 900 000 €. Délais clients : 60 jours. Charges fixes mensuelles : 55 000 €.',
      items: [
        'T1 (janv-mars) : Charges 165 000 € / Encaissements 140 000 € (CA d\'oct-nov N-1) → Tension -25 000 €',
        'T2 (avr-juin) : Charges 165 000 € + cotisations 12 000 € / Encaissements 185 000 € → Légèrement positif',
        'T3 (juil-sept) : Pleine saison → solde s\'améliore, reconstitution de réserve',
        'T4 (oct-déc) : Ralentissement → nouveau creux à anticiper',
        'Résultat : 2 périodes de tension prévisibles → 2 négociations bancaires à préparer en avance',
      ],
    },
    {
      type: 'midcta',
      title: 'Deux périodes de tension prévisibles par an — préparez-les avant qu\'elles arrivent',
      content: 'Trezoone construit votre plan de trésorerie PME sur 12 mois et identifie automatiquement vos mois critiques.',
    },
    {
      type: 'checklist',
      title: 'Checklist plan de trésorerie PME',
      items: [
        'Analyser les délais de paiement réels de chaque client (pas juste les conditions contractuelles)',
        'Inclure les charges sociales et fiscales trimestrielles (TVA, IS, cotisations)',
        'Intégrer les remboursements d\'emprunt (capital + intérêts)',
        'Prévoir les investissements et amortissements',
        'Modéliser la saisonnalité de l\'activité',
        'Construire 3 scénarios : optimiste, réaliste, dégradé',
        'Identifier les mois critiques et prévoir le financement en avance',
        'Mettre à jour chaque mois avec les données réelles',
      ],
    },
    {
      type: 'solution',
      title: 'Trezoone automatise votre plan de trésorerie PME',
      content: 'Fini le fichier Excel fragile. Trezoone construit et maintient votre plan automatiquement.',
      items: [
        'Connexion à votre banque professionnelle ou import de relevés',
        'Catégorisation automatique des charges et encaissements',
        'Plan de trésorerie sur 12 mois mis à jour en temps réel',
        'Module PME : suivi des factures clients et délais de paiement',
        'Calcul automatique du BFR et alerte en cas de dégradation',
        'Rapport PDF pour vos rendez-vous bancaires',
      ],
    },
    {
      type: 'cluster',
      title: 'Approfondir la gestion financière de votre PME',
      links: [
        { label: 'Gestion trésorerie TPE/PME', href: '/gestion-tresorerie-tpe' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
        { label: 'BFR et trésorerie', href: '/bfr-tresorerie' },
        { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
        { label: 'Suivi trésorerie entreprise', href: '/suivi-tresorerie-entreprise' },
        { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
      ],
    },
    {
      type: 'cta',
      title: 'Votre plan de trésorerie PME, prêt en 30 minutes',
      content: 'Importez vos données bancaires et obtenez votre plan sur 12 mois.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion trésorerie TPE/PME', href: '/gestion-tresorerie-tpe' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
    { label: 'BFR et trésorerie', href: '/bfr-tresorerie' },
    { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
    { label: 'Suivi trésorerie entreprise', href: '/suivi-tresorerie-entreprise' },
    { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
    { label: 'Modèle plan trésorerie', href: '/modele-plan-tresorerie' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }
export default function PlanTresoreriePMEPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
